import { useState } from "react";
import { cn } from "src/lib/utils";

// 비디오 데이터
const videoData = {
  category: "외부활동",
  title: "[서울경제TV] 조영구의 트렌드 핫이슈 - 조근호 대표가 말하다",
  videoUrl: "https://www.youtube.com/embed/RJ2uAIHJYUM",
  thumbnailUrl:
    "https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/insight-section/thumnail.webp",
  date: "2026.02.07",
};

// VideoCard 컴포넌트 (내부 컴포넌트)
interface VideoCardProps {
  category: string;
  title: string;
  videoUrl: string;
  thumbnailUrl: string;
  date: string;
}

function VideoCard({
  category,
  title,
  videoUrl,
  thumbnailUrl,
  date,
}: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  // YouTube URL에서 embed URL로 변환 (autoplay 추가)
  const getEmbedUrl = (url: string) => {
    // 이미 embed URL인 경우 autoplay 파라미터 추가
    if (url.includes("embed")) {
      return `${url}?autoplay=1`;
    }
    // 일반 YouTube URL인 경우 embed URL로 변환
    const videoId = url.split("v=")[1] || url.split("/").pop();
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  };

  if (isPlaying) {
    return (
      <article className="bg-[#F4F4F4] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow tablet:col-span-2">
        <div className="relative w-full aspect-video">
          <iframe
            src={getEmbedUrl(videoUrl)}
            title={title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </article>
    );
  }

  return (
    <article className="bg-[#F4F4F4] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow tablet:col-span-2">
      {/* 썸네일 컨테이너 - 16:9 비율 유지 */}
      <div
        className="relative w-full aspect-video cursor-pointer group"
        onClick={handlePlay}
        style={{
          backgroundImage: `url('${thumbnailUrl}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* 오버레이 (기본: 어둡게, 호버: 밝게) */}
        <div className="absolute inset-0 bg-black/50 group-hover:bg-black/20 transition-all duration-300" />

        {/* 컨텐츠 래퍼 */}
        <div className="relative h-full p-4 desktop:px-6 desktop:py-8 flex flex-col">
          {/* 상단 정보 */}
          <div className="flex-grow">
            <span className="hidden tablet:inline-block text-white tablet:text-heading-3-mb desktop:text-heading-3 mb-2 underline">
              {category}
            </span>
            <h3 className="text-white text-heading-2-mb tablet:text-heading-1-mb desktop:text-heading-1 line-clamp-1 drop-shadow-lg">
              {title}
            </h3>
          </div>

          {/* 중앙 재생 버튼 - S3 SVG 이미지 사용 */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img
              src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/insight-section/play-button.svg"
              alt="재생 버튼"
              loading="lazy"
              decoding="async"
              className="w-20 desktop:w-36 group-hover:scale-105 transition-transform duration-200"
            />
          </div>

          {/* 하단 날짜 */}
          <div className="flex justify-between items-end">
            <time className="text-white !text-label-1-mb desktop:!text-label-1">
              {date}
            </time>
          </div>
        </div>
      </div>
    </article>
  );
}

// 기사 데이터
const articles = [
  {
    id: 1,
    category: "NEWS",
    title: '[이투뉴스] "10건 중 4건이 지방 수임" 채권도시 법률사무소',
    date: "2026.01.06",
    link: "https://www.e2news.com/news/articleView.html?idxno=326675",
    description:
      "‘건강한 채권 생태계’를 목표로 ESG 경영을 실천 중인 채권도시 법률사무소(조근호, 황인규 대표 변호사)는 5일, 혁신적인 온라인(비대면) 법률 서비스가 지역 간 법률 서비스 격차를 획기적으로 해소하고 있다고 밝혔다.",
  },
  {
    id: 2,
    category: "SNS",
    title:
      '[Youtube] "수임료 지원 평균 500만원" 소송 부담을 줄이기 위한 채권도시 법률사무소',
    date: "2025.09.12",
    link: "https://youtube.com/shorts/hcoDs3tSjY4",
    description:
      "수임료가 부담되어 소중한 채권자산을 포기하는 사람들을 위해 채권도시 법률사무소는 공익적인 측면의 수임료 지원 시스템을 발표했습니다.",
  },
  {
    id: 3,
    category: "NEWS",
    title:
      '[ESG경제] "빌려준 돈, 포기하지 마세요" 채권도시, 맞춤형 추심 솔루션 제공',
    date: "2025.08.28",
    link: "https://www.esgeconomy.com/news/articleView.html?idxno=12558",
    description:
      "조근호 대표는 “수임료가 없어도 소송을 포기할 필요는 없습니다. AI가 승소 가능성과 회수율을 분석하고, 소송금융이 실행을 돕습니다. 채권자는 빠르게 소송을 시작해 높은 회수율을 기대할 수 있습니다.",
  },
  {
    id: 4,
    category: "NEWS",
    title: "[이넷뉴스] [칼럼] 소액 채권 관리의 중요성과 효율적인 회수 전략",
    date: "2025.08.27",
    link: "https://www.enetnews.co.kr/news/articleView.html?idxno=41557",
    description:
      "채권도시 법률사무소는 이번 성과를 통해 소송 절차 감소로 인한 사법·행정 비용 절감, 장기 연체 방지에 따른 가계 파탄 예방, 금융 신뢰 회복 등 사회 전반의 긍정적 효과를 만들어내고 있다. 특히 채무자의 경제활동 복귀와 사회 재진입을 지원함으로써 파산을 줄이고 노동시장 복귀를 촉진해 국가 세수 및 내수경제 활성화에도 기여했다는 평가다.",
  },
];

export function InsightSection() {
  return (
    <section
      className="py-20 desktop:py-[76px] bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/insight-section/bg.webp')`,
      }}
    >
      <div className="px-[16px] tablet:px-[32px] desktop:px-0 max-w-[1150px] mx-auto">
        {/* 제목 */}
        <div className="text-center mb-6 tablet:mb-10 desktop:mb-9">
          <h2 className="text-heading-1-mb desktop:text-heading-1 text-[#212121] mb-5">
            채권도시 INSIGHT
          </h2>
          <p className="text-body-1-mb desktop:text-body-1 text-[#4F4F4F]">
            더 나은 법률 서비스를 위한 기록
          </p>
        </div>

        {/* 컨텐츠 영역 */}
        <div className="flex flex-col gap-4 tablet:grid tablet:grid-cols-3">
          {/* 비디오 카드 */}
          <VideoCard
            category={videoData.category}
            title={videoData.title}
            videoUrl={videoData.videoUrl}
            thumbnailUrl={videoData.thumbnailUrl}
            date={videoData.date}
          />
          {articles.map((article, index) => {
            const isLast = index === articles.length - 1;
            const isFirst = index === 0;

            return (
              <a
                key={article.id}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <article
                  className={cn(
                    "rounded-xl p-4 tablet:p-6 desktop:px-6 desktop:py-8 shadow-sm hover:shadow-md transition-all cursor-pointer group border-2 size-full flex flex-col ",
                    isLast
                      ? "bg-[#506286] border-[#506286] hover:bg-[#293D67] hover:border-[#293D67]"
                      : "bg-[#F4F4F4] border-[#F4F4F4] hover:bg-white hover:border-c-primary",
                  )}
                >
                  {/* 컨텐츠 영역 - 남은 공간 모두 차지 */}
                  <div className="flex-grow desktop:mb-12">
                    <div className="hidden tablet:block mb-2">
                      <span
                        className={cn(
                          "text-heading-3-mb underline",
                          isLast ? "text-white" : "text-c-primary",
                        )}
                      >
                        {article.category}
                      </span>
                    </div>
                    <h3
                      className={cn(
                        "!text-heading-2-mb tablet:!text-heading-1-mb tablet:mb-4 line-clamp-2",
                        isLast
                          ? "text-white"
                          : "text-[#41444B] group-hover:text-c-primary",
                      )}
                    >
                      {article.title}
                    </h3>
                    <p
                      className={cn(
                        "hidden tablet:block tablet:text-body-1-mb desktop:text-body-1",
                        isFirst ? "tablet:line-clamp-3" : "tablet:line-clamp-2",
                        isLast
                          ? "text-white"
                          : "text-[#41444B] group-hover:text-c-primary",
                      )}
                    >
                      {article.description}
                    </p>
                  </div>

                  {/* 날짜 - 항상 하단 고정 */}
                  <time
                    className={cn(
                      "!text-label-1-mb desktop:!text-label-1 transition-colors mt-3",
                      isLast
                        ? "text-white"
                        : "text-[#506286] group-hover:text-c-primary",
                    )}
                  >
                    {article.date}
                  </time>
                </article>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
